// The stock ledger: every change to a drug's or medical consumable's quantity, and
// every check of one, newest first (GET /stock/history; core/stock.py).
//
// At most 1000 lines, like the audit log — the filters are how you get to what you
// want: which item, which batch, what happened, where, who, when. Searching a batch
// number also opens the batch lookup above the ledger (GET /stock/batches): where that
// batch is now and in whose hands, and the incidents it was given on — the answer to a
// supplier's recall notice. It names incidents, never patients; opening one is the
// incident's own permission.
//
// Reached from the Inventory page (a hidden route), often with a filter already set
// in the query string: ?batch_id=, ?item_id=, ?type_id=.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Alert, Autocomplete, Box, Button, Chip, Link, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReportIcon from "@mui/icons-material/Report";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { useMe } from "../MeContext";
import { BATCHED_CATEGORIES, CONTAINER_CATEGORY, typeLabel } from "../constants";
import { KIND_LABEL, KIND_OPTIONS, containerPath, signed } from "../stockRules";
import { exportStockHistory } from "../exportExcel";
import { downloadDocument } from "../download";

const STOCK_API = `${API_BASE_URL}/stock`;
const LIMIT = 1000;

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const BLANK = { type_ids: [], batch_number: "", kinds: [], item_id: "", actor: "", from: "", to: "" };

// What a line says beyond its numbers — the reason, the incident, whose hands, the
// count — composed once so the table and its tooltip agree.
const detail = (r) => [
  r.expected != null && r.counted != null && r.expected !== r.counted
    ? `counted ${r.counted}, record said ${r.expected}` : null,
  r.expected != null && r.expected === r.counted ? `counted ${r.counted}` : null,
  r.reason,
  r.personnel_name ? `${r.kind === "return" ? "from" : "to"} ${r.personnel_name}` : null,
  r.event_label,
  r.handled_by ? `handled by ${r.handled_by}` : null,
  r.notes,
].filter(Boolean).join(" · ");

export default function StockHistory() {
  const { can } = useMe();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState(BLANK);
  const [batchId, setBatchId] = useState(params.get("batch_id") || "");
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState(null);   // the batch lookup, when one was asked
  const [loading, setLoading] = useState(false);
  const [recall, setRecall] = useState(null);     // { batch, note }

  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const stockTypes = types.filter(t => BATCHED_CATEGORIES.includes(t.category))
    .sort((a, b) => a.name.localeCompare(b.name));
  const containers = items.filter(i => typeById[i.type_id]?.category === CONTAINER_CATEGORY
                                      || BATCHED_CATEGORIES.includes(typeById[i.type_id]?.category))
    .sort((a, b) => containerPath(a, itemById, typeById).localeCompare(containerPath(b, itemById, typeById)));

  const load = (f = filters, bId = batchId) => {
    const q = new URLSearchParams();
    f.type_ids.forEach(t => q.append("type_id", t));
    f.kinds.forEach(k => q.append("kind", k));
    if (f.batch_number.trim()) q.set("batch_number", f.batch_number.trim());
    if (bId) q.set("batch_id", bId);
    if (f.item_id) q.set("item_id", f.item_id);
    if (f.actor.trim()) q.set("actor", f.actor.trim());
    if (f.from) q.set("from", new Date(f.from).toISOString());
    if (f.to) q.set("to", new Date(f.to).toISOString());
    q.set("limit", LIMIT);
    setLoading(true);
    axios.get(`${STOCK_API}/history?${q}`)
      .then(res => setRows(res.data))
      .finally(() => setLoading(false));
    // The batch lookup answers "where is it now?", which the ledger alone doesn't.
    if (bId || f.batch_number.trim()) {
      axios.get(`${STOCK_API}/batches`, { params: bId ? { batch_id: bId } : { batch_number: f.batch_number.trim() } })
        .then(res => setBatches(res.data));
    } else {
      setBatches(null);
    }
  };

  useEffect(() => {
    axios.get(`${API_BASE_URL}/inventory/items/`).then(res => setItems(res.data));
    axios.get(`${API_BASE_URL}/inventory/types/`).then(res => setTypes(res.data));
    const initial = {
      ...BLANK,
      item_id: params.get("item_id") ? Number(params.get("item_id")) : "",
      type_ids: params.get("type_id") ? [Number(params.get("type_id"))] : [],
    };
    setFilters(initial);
    load(initial, params.get("batch_id") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setFilters(f => ({ ...f, ...patch }));
  const search = () => { setBatchId(""); load(filters, ""); };
  const clear = () => { setFilters(BLANK); setBatchId(""); load(BLANK, ""); };

  const saveRecall = () => {
    axios.post(`${STOCK_API}/batches/${recall.batch.id}/recall`, { note: recall.note })
      .then(res => {
        setBatches(bs => bs.map(b => (b.id === res.data.id ? res.data : b)));
        notify(recall.note.trim() ? "Batch marked as recalled." : "Recall flag cleared.", "success");
        setRecall(null);
      }).catch(() => {});
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/inventory-items")}>Inventory</Button>
        <Typography variant="h4" sx={{ flex: 1 }}>Stock history</Typography>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={rows.length === 0}
                onClick={() => exportStockHistory(rows, { kindLabel: k => KIND_LABEL[k] || k })}>
          Export to Excel
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Autocomplete multiple size="small" sx={{ minWidth: 260, flex: 1 }}
                      options={stockTypes} getOptionLabel={typeLabel}
                      value={stockTypes.filter(t => filters.type_ids.includes(t.id))}
                      onChange={(_, v) => set({ type_ids: v.map(t => t.id) })}
                      renderInput={p => <TextField {...p} label="Item" />} />
        <TextField label="Batch number" size="small" value={filters.batch_number}
                   onChange={e => set({ batch_number: e.target.value })}
                   onKeyDown={e => e.key === "Enter" && search()} />
        <TextField label="What happened" select size="small" sx={{ minWidth: 200 }}
                   slotProps={{ select: { multiple: true,
                     renderValue: v => (v.length ? v.map(k => KIND_LABEL[k]).join(", ") : "Everything") } }}
                   value={filters.kinds} onChange={e => set({ kinds: e.target.value })}>
          {KIND_OPTIONS.map(k => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
        </TextField>
        <Autocomplete size="small" sx={{ minWidth: 260, flex: 1 }}
                      options={containers} getOptionKey={c => c.id}
                      getOptionLabel={c => containerPath(c, itemById, typeById)}
                      value={itemById[filters.item_id] || null}
                      onChange={(_, c) => set({ item_id: c ? c.id : "" })}
                      renderInput={p => <TextField {...p} label="Where (and everything inside)" />} />
        <TextField label="Who" size="small" value={filters.actor}
                   onChange={e => set({ actor: e.target.value })}
                   onKeyDown={e => e.key === "Enter" && search()} />
        <TextField label="From" type="datetime-local" size="small"
                   slotProps={{ inputLabel: { shrink: true } }} value={filters.from}
                   onChange={e => set({ from: e.target.value })} />
        <TextField label="To" type="datetime-local" size="small"
                   slotProps={{ inputLabel: { shrink: true } }} value={filters.to}
                   onChange={e => set({ to: e.target.value })} />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={search}>Search</Button>
        <Button onClick={clear}>Clear</Button>
      </Stack>

      {batches && (
        <Stack spacing={2}>
          {batches.length === 0 && <Alert severity="info">No batch matches that number.</Alert>}
          {batches.map(b => (
            <Paper key={b.id} elevation={0} sx={{ border: "1px solid", borderColor: b.recalled_at ? "error.main" : "divider", p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }} useFlexGap>
                <Typography variant="h6">{b.type_name} — batch {b.batch_number || "(not recorded)"}</Typography>
                <Typography color="text.secondary">expires {b.expiry_date || "—"} · {b.held} held now</Typography>
                {b.recalled_at && <Chip color="error" icon={<ReportIcon />} label={`Recalled: ${b.recall_note}`} />}
                <Box sx={{ flex: 1 }} />
                {can("Stock.dispose") && (
                  <Button size="small" color={b.recalled_at ? "inherit" : "error"}
                          onClick={() => setRecall({ batch: b, note: "" })}>
                    {b.recalled_at ? "Clear recall" : "Mark recalled"}
                  </Button>
                )}
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Where it is now</Typography>
              {b.holdings.length === 0
                ? <Typography variant="body2" color="text.secondary">None of it is held anywhere.</Typography>
                : b.holdings.map(h => (
                  <Typography key={h.id} variant="body2">
                    {h.quantity}{h.opened ? ` (${h.opened} opened)` : ""} at {h.where}
                    {h.held_by && <strong> — out with {h.held_by}</strong>}
                  </Typography>
                ))}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Given to patients</Typography>
              {b.administrations.length === 0
                ? <Typography variant="body2" color="text.secondary">Not recorded as given on any incident.</Typography>
                : b.administrations.map(a => (
                  <Typography key={a.administration_id} variant="body2"
                              sx={a.voided ? { textDecoration: "line-through", color: "text.secondary" } : undefined}>
                    {a.part_unit ? "part of 1" : a.quantity} on{" "}
                    <Link href={`/incidents?open=${a.incident_id}`} target="_blank" rel="noopener"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      incident #{a.incident_id}<OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                    {a.event_label ? ` (${a.event_label})` : ""} — {fmtWhen(a.given_at)}
                    {a.given_by ? `, ${a.given_by}` : ""}{a.voided ? " — voided" : ""}
                  </Typography>
                ))}
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="body2" color="text.secondary">
        {loading ? "Loading…" : `${rows.length} line${rows.length === 1 ? "" : "s"}`}
        {rows.length === LIMIT && " (showing the most recent 1000 — narrow the filters to see more)"}
      </Typography>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>When</TableCell>
                <TableCell>What</TableCell>
                <TableCell>Item / batch</TableCell>
                <TableCell>Where</TableCell>
                <TableCell align="right">Change</TableCell>
                <TableCell align="right">Held after</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtWhen(r.at)}</TableCell>
                  <TableCell>
                    {KIND_LABEL[r.kind] || r.kind}
                    {r.needs_investigation && r.kind === "adjust" && (
                      <Chip size="small" color="warning" label="Investigate" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.type_name}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      batch{" "}
                      <Link component="button" type="button"
                            onClick={() => { setBatchId(String(r.batch_id)); load(filters, String(r.batch_id)); }}>
                        {r.batch_number || "(not recorded)"}
                      </Link>
                      {r.expiry_date ? `, exp ${r.expiry_date}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.item_label || "—"}</TableCell>
                  <TableCell align="right"
                             sx={{ color: r.change < 0 ? "error.main" : r.change > 0 ? "success.main" : undefined,
                                   fontWeight: 600 }}>
                    {signed(r.change)}
                    {r.opened_change ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {signed(r.opened_change)} opened
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{r.balance_after ?? ""}</TableCell>
                  <TableCell>{r.actor_name || r.actor_email || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {r.incident_ref != null && (
                        <Link href={`/incidents?open=${r.incident_ref}`} target="_blank" rel="noopener" sx={{ mr: 0.5 }}>
                          incident #{r.incident_ref}
                        </Link>
                      )}
                      {detail(r)}
                    </Typography>
                    {(r.reference || r.document_id) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {r.reference ? `Ref ${r.reference}` : ""}
                        {r.document_id && (
                          <Tooltip title="Download the attached note">
                            <Link component="button" type="button" sx={{ ml: r.reference ? 1 : 0 }}
                                  onClick={() => downloadDocument(
                                    { id: r.document_id, original_filename: r.document_name },
                                    `${STOCK_API}/documents`).catch(() => {})}>
                              {r.document_name || "document"}
                            </Link>
                          </Tooltip>
                        )}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    Nothing matches those filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!recall} onClose={() => setRecall(null)} fullWidth maxWidth="sm">
        <DialogTitle>{recall?.batch.recalled_at ? "Clear the recall?" : "Mark this batch recalled"}</DialogTitle>
        <DialogContent>
          {recall?.batch.recalled_at ? (
            <DialogContentText>
              The batch stops being flagged as recalled everywhere it's held.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Flags the batch wherever it's held, including in the Give a drug picker.
                Nothing is moved — set it aside for disposal from the Inventory page.
              </DialogContentText>
              <TextField label="Supplier notice / reason" fullWidth multiline autoFocus required
                         value={recall?.note || ""}
                         onChange={e => setRecall(r => ({ ...r, note: e.target.value }))} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecall(null)}>Cancel</Button>
          <Button variant="contained" color="error"
                  disabled={!recall?.batch.recalled_at && !(recall?.note || "").trim()}
                  onClick={saveRecall}>
            {recall?.batch.recalled_at ? "Clear recall" : "Mark recalled"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
